import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { merge, omit, orderBy } from "lodash";
import {
  type DialerViewModeType,
  type DialerActiveCall,
  type DialerCallerDetail,
} from "./types";
import {
  DIALER_CALL_STATE_ON_HOLD,
  DIALER_CALL_STATE_PICKED_UP,
  DIALER_CALL_STATE_QUEUED,
  DIALER_INCOMING_CALL_TYPE,
  DIALER_OUTGOING_CALL_TYPE,
  DIALER_VIEW_MODE_COLLAPSED,
  DIALER_VIEW_MODE_EXPANDED,
} from "./constants";
import { getCallWaitingIds, isStateValid } from "./utils";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";
import { type Patient } from "services/types/patients.type";
import { type UserSettingsInput } from "services/suno-comms/types/user-settings.type";

export interface SunoDialerState {
  displaySettings: {
    defaultDisplayMode: DialerViewModeType;
    showQueue: boolean;
    showSettings: boolean;
    voiceSettings: Partial<UserSettingsInput>;
  };
  callsQueue: {
    ids: string[];
    waitingIds: string[];
    details: Record<string, DialerCallerDetail>;
  };
  outgoingCall:
    | {
        id?: string;
        phone_number?: string;
        patient?: Patient;
        dialInProgress?: boolean;
        displayPatients?: boolean;
      }
    | undefined;
  incomingCall:
    | {
        id: string;
        displayMode: DialerViewModeType;
      }
    | undefined;
  activeCall: DialerActiveCall;
  ignoreCallSids: Record<string, boolean> | undefined;
  createCallDetails: {
    isCreateExecuted: boolean;
    isShouldExecuteUpdate: boolean;
    callSid: string;
  };
}

export const sunoDialerStorage = setupLocalStorageAPI("sunoDialer");

export const userVoiceSettingsStorage = setupLocalStorageAPI(
  "user-voice-settings"
);

const initialState: SunoDialerState = {
  displaySettings: {
    defaultDisplayMode: DIALER_VIEW_MODE_COLLAPSED,
    showQueue: false,
    showSettings: false,
    voiceSettings: userVoiceSettingsStorage.getData({
      ring_web_app: true,
      ring_mobile_app: false,
      do_not_disturb: false,
      block_unknown_number: false,
      notify_email: false,
      call_forwarding: false,
      forward_to: "",
      default_microphone: "default",
      default_speaker: "default",
      default_volume: "100",
    }),
  },
  callsQueue: {
    ids: [],
    waitingIds: [],
    details: {},
  },
  outgoingCall: undefined,
  incomingCall: undefined,
  activeCall: undefined,
  ignoreCallSids: {},
  createCallDetails: {
    isCreateExecuted: false,
    isShouldExecuteUpdate: false,
    callSid: "",
  },
};

export const dialerSlice = createSlice({
  name: "dialer",
  initialState,
  reducers: {
    sunoDialerInit: (state, action: PayloadAction<SunoDialerState>) => {
      const newState = isStateValid(action.payload)
        ? action.payload
        : initialState;

      return newState;
    },
    loadCallQueueAction: (
      state,
      action: PayloadAction<DialerCallerDetail[]>
    ) => {
      const calls = action.payload;
      const ids = [
        ...new Set<string>(orderBy(calls, "id", "asc").map((item) => item.id)),
      ];

      state.callsQueue = {
        ids,
        waitingIds: [],
        details: calls.reduce(
          (acc: Record<string, DialerCallerDetail>, item) => {
            acc[item.id] = {
              ...item,
              type: item.type || DIALER_INCOMING_CALL_TYPE,
              state: item.state || DIALER_CALL_STATE_QUEUED,
            };
            return acc;
          },
          {}
        ),
      };

      state.callsQueue.waitingIds = getCallWaitingIds(state, ids);
      state.outgoingCall = undefined;

      const firstId = state.callsQueue.waitingIds[0];

      if (!firstId) {
        state.incomingCall = undefined;
      } else if (
        !state.incomingCall ||
        !ids.includes(String(state.incomingCall?.id))
      ) {
        state.incomingCall = {
          id: firstId,
          displayMode: state.displaySettings.defaultDisplayMode,
        };
      }

      if (!ids.includes(String(state.activeCall?.id))) {
        state.activeCall = undefined;
      }
    },

    removeCallsInProgressAction: (state) => {
      const activeCallId = state.activeCall?.id;
      const outgoingCallId = state.outgoingCall?.id;
      const incomingCallId = state.incomingCall?.id;

      const ids = state.callsQueue.ids.filter(
        (callId) =>
          callId !== activeCallId &&
          callId !== outgoingCallId &&
          callId !== incomingCallId
      );

      state.callsQueue = {
        ids,
        waitingIds: getCallWaitingIds(state, ids),
        details: omit(state.callsQueue.details, [
          String(activeCallId),
          String(outgoingCallId),
          String(incomingCallId),
        ]),
      };

      state.callsQueue.waitingIds = getCallWaitingIds(state, ids);

      state.outgoingCall = undefined;
      state.activeCall = undefined;
      state.incomingCall = undefined;
      state.createCallDetails = {
        callSid: "",
        isCreateExecuted: false,
        isShouldExecuteUpdate: false,
      };
    },

    removeCallFromQueueAction: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const ids = state.callsQueue.ids.filter((callId) => callId !== id);

      state.callsQueue = {
        ids,
        waitingIds: getCallWaitingIds(state, ids),
        details: omit(state.callsQueue.details, id),
      };

      if (state.outgoingCall?.id === id) {
        state.outgoingCall = undefined;
      }

      state.callsQueue.waitingIds = getCallWaitingIds(state, ids);

      if (state.incomingCall?.id === id) {
        state.incomingCall = undefined;
      }

      if (state.activeCall?.id === id) {
        state.activeCall = undefined;
      }

      if (state.activeCall?.id === id || state.incomingCall?.id === id) {
        state.createCallDetails = {
          callSid: "",
          isCreateExecuted: false,
          isShouldExecuteUpdate: false,
        };
      }
    },

    removeIncomingCallFromQueueAction: (state) => {
      state.incomingCall = undefined;
    },

    setOutgoingCallPhoneNumberAction: (
      state,
      action: PayloadAction<{
        phoneNumber?: string;
      }>
    ) => {
      const { phoneNumber } = action.payload;

      if (state.outgoingCall) {
        state.outgoingCall.phone_number = phoneNumber;
      } else {
        state.outgoingCall = {
          phone_number: phoneNumber,
        };
      }
      state.createCallDetails = {
        callSid: "",
        isCreateExecuted: false,
        isShouldExecuteUpdate: false,
      };
    },

    setOutgoingCallPatientAction: (
      state,
      action: PayloadAction<{
        patient?: Patient;
      }>
    ) => {
      const { patient } = action.payload;

      if (state.outgoingCall) {
        state.outgoingCall.patient = patient;
      } else {
        state.outgoingCall = {
          patient,
        };
      }
      state.createCallDetails = {
        callSid: "",
        isCreateExecuted: false,
        isShouldExecuteUpdate: false,
      };
    },

    toggleOutgoingCallDisplayPatientsAction: (state) => {
      if (!state.outgoingCall) {
        state.outgoingCall = {
          displayPatients: true,
        };
      } else {
        state.outgoingCall.displayPatients = !Boolean(
          state.outgoingCall.displayPatients
        );
      }
    },

    addCallToIgnoreList: (
      state,
      action: PayloadAction<{
        sid: string;
      }>
    ) => {
      if (!state.ignoreCallSids) {
        state.ignoreCallSids = {
          [action.payload.sid]: true,
        };
      } else {
        state.ignoreCallSids = {
          ...state.ignoreCallSids,
          [action.payload.sid]: true,
        };
      }
    },

    outgoingCallDisplayDigitsAction: (state) => {
      if (!state.outgoingCall) {
        state.outgoingCall = {
          displayPatients: false,
        };
      } else {
        state.outgoingCall.displayPatients = false;
      }
    },

    setOutgoingCallIsDialingAction: (
      state,
      action: PayloadAction<{
        id?: string;
        phoneNumber?: string;
        userId?: number | null;
        patient?: Patient | null;
      }>
    ) => {
      if (!state.outgoingCall) return;
      const { id, userId, patient, phoneNumber } = action.payload;
      state.outgoingCall.dialInProgress = true;
      state.outgoingCall.displayPatients = false;

      if (id) {
        state.outgoingCall.id = id;
        state.callsQueue.details[id] = {
          id,
          callerId: patient?.id,
          extra: merge(
            patient
              ? {
                  avatar_url: patient?.avatar ?? "",
                  full_name: patient?.fullName,
                  phone_number: patient?.phone,
                  segment: patient?.crm_segment,
                  created_at_display: patient?.created_at_display,
                  gender: patient?.sex,
                  age_years: patient?.age_years,
                }
              : undefined,
            { phone_number: phoneNumber }
          ),
          type: DIALER_OUTGOING_CALL_TYPE,
          state: DIALER_CALL_STATE_QUEUED,
          userId,
        };
        const ids = [...new Set<string>([...state.callsQueue.ids, id])];
        state.callsQueue.ids = ids;
        state.callsQueue.waitingIds = getCallWaitingIds(state, ids);
      }
    },

    pickUpCallAction: (
      state,
      action: PayloadAction<{
        id: string;
        userId?: number | null;
      }>
    ) => {
      const { id, userId } = action.payload;

      const callDetails = state.callsQueue.details[id];

      if (!callDetails || !state.callsQueue?.ids.includes(id)) return;

      state.displaySettings.defaultDisplayMode = DIALER_VIEW_MODE_EXPANDED;

      state.callsQueue.details[id] = {
        ...callDetails,
        userId,
        state: DIALER_CALL_STATE_PICKED_UP,
        pickedUpAt: new Date().toISOString(),
      };

      state.callsQueue.waitingIds = getCallWaitingIds(
        state,
        state.callsQueue.ids
      );

      state.activeCall = {
        id,
        displayMode: DIALER_VIEW_MODE_EXPANDED,
        isMuted: false,
      };

      if (state.outgoingCall) {
        state.outgoingCall.id = undefined;
        state.outgoingCall.dialInProgress = false;
      }

      const firstId = state.callsQueue.waitingIds[0];
      if (firstId) {
        state.incomingCall = {
          id,
          displayMode: state.displaySettings.defaultDisplayMode,
        };
      } else {
        state.incomingCall = undefined;
      }
    },

    updateCreateCallDetailsAction(
      state,
      action: PayloadAction<{
        callSid?: string;
        isCreateExecuted?: boolean;
        isShouldExecuteUpdate?: boolean;
      }>
    ) {
      const { callSid, isCreateExecuted, isShouldExecuteUpdate } =
        action.payload;
      const newCreateCallDetails = { ...state.createCallDetails };

      if (callSid !== undefined) {
        newCreateCallDetails.callSid = callSid;
      }
      if (isCreateExecuted !== undefined) {
        newCreateCallDetails.isCreateExecuted = isCreateExecuted;
      }
      if (isShouldExecuteUpdate !== undefined) {
        newCreateCallDetails.isShouldExecuteUpdate = isShouldExecuteUpdate;
      }
      state.createCallDetails = newCreateCallDetails;
    },

    swapCallAction: (state, action: PayloadAction<{ toId: string }>) => {
      const { toId } = action.payload;

      const toCall = state.callsQueue.details[toId];

      if (!toCall) return;

      const fromId = state.activeCall?.id || state.incomingCall?.id;

      if (fromId && state.activeCall) {
        state.callsQueue.details[fromId].state = DIALER_CALL_STATE_ON_HOLD;

        if (toCall.state === DIALER_CALL_STATE_QUEUED) {
          state.incomingCall = {
            id: toId,
            displayMode: state.displaySettings.defaultDisplayMode,
          };
          state.activeCall = undefined;
        } else {
          state.activeCall = {
            id: toId,
            displayMode: DIALER_VIEW_MODE_EXPANDED,
            isMuted: false,
          };
        }
      } else if (fromId && toCall.state === DIALER_CALL_STATE_QUEUED) {
        state.incomingCall = {
          id: toId,
          displayMode: state.displaySettings.defaultDisplayMode,
        };
      } else if (fromId && toCall?.state !== DIALER_CALL_STATE_QUEUED) {
        state.callsQueue.details[toId].state = DIALER_CALL_STATE_ON_HOLD;
        state.incomingCall = undefined;
        state.activeCall = {
          id: toId,
          displayMode: DIALER_VIEW_MODE_EXPANDED,
          isMuted: false,
        };
      }

      const ids = state.callsQueue.ids;

      state.callsQueue.waitingIds = getCallWaitingIds(state, ids);
    },

    setDefaultDisplayMode: (
      state,
      action: PayloadAction<{ mode: DialerViewModeType }>
    ) => {
      const { mode } = action.payload;
      state.displaySettings.defaultDisplayMode = mode;

      if (state.incomingCall) {
        state.incomingCall.displayMode = mode;
      }

      if (state.activeCall) {
        state.activeCall.displayMode = mode;
      }
    },

    toggleExpandAction: (state) => {
      const mode =
        state.displaySettings.defaultDisplayMode === DIALER_VIEW_MODE_EXPANDED
          ? DIALER_VIEW_MODE_COLLAPSED
          : DIALER_VIEW_MODE_EXPANDED;

      state.displaySettings.defaultDisplayMode = mode;

      if (state.incomingCall) {
        state.incomingCall.displayMode = mode;
      }

      if (state.activeCall) {
        state.activeCall.displayMode = mode;
      }
    },

    toggleShowSettingsAction: (state) => {
      const mode = !state.displaySettings.showSettings;
      state.displaySettings.showSettings = mode;
    },

    setVoiceSettingsAction: (
      state,
      action: PayloadAction<Partial<UserSettingsInput>>
    ) => {
      state.displaySettings.voiceSettings = action.payload;
      userVoiceSettingsStorage.updateData(action.payload);
    },

    toggleShowQueueAction: (state) => {
      const mode = !state.displaySettings.showQueue;
      state.displaySettings.showQueue = mode;
    },

    toggleMuteActiveCallAction: (
      state,
      action: PayloadAction<{ shouldMute: boolean }>
    ) => {
      if (!state.activeCall) return;
      state.activeCall.isMuted = action?.payload.shouldMute;
    },

    resumeCallAction: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!state.callsQueue.details[id]) return;
      state.callsQueue.details[id].state = DIALER_CALL_STATE_PICKED_UP;
    },

    onHoldCallAction: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!state.callsQueue.details[id]) return;

      Object.assign(state.callsQueue, {
        details: {
          ...state.callsQueue.details,
          [id]: {
            ...state.callsQueue.details[id],
            state: DIALER_CALL_STATE_ON_HOLD,
          },
        },
      });

      if (state.activeCall?.id === id) {
        state.activeCall.isMuted = false;
      }
    },
  },
});

export const {
  loadCallQueueAction,
  removeCallFromQueueAction,
  removeIncomingCallFromQueueAction,
  setOutgoingCallPhoneNumberAction,
  setOutgoingCallPatientAction,
  toggleOutgoingCallDisplayPatientsAction,
  outgoingCallDisplayDigitsAction,
  setOutgoingCallIsDialingAction,
  pickUpCallAction,
  updateCreateCallDetailsAction,
  swapCallAction,
  toggleExpandAction,
  toggleShowQueueAction,
  toggleMuteActiveCallAction,
  resumeCallAction,
  onHoldCallAction,
  sunoDialerInit,
  removeCallsInProgressAction,
  setDefaultDisplayMode,
  addCallToIgnoreList,
  toggleShowSettingsAction,
  setVoiceSettingsAction,
} = dialerSlice.actions;

const dialerSelector = (state: RootState) => state.dialer;

export const selectDialer = createSelector(dialerSelector, (dialer) => dialer);

export const selectDialerDisplaySettings = createSelector(
  dialerSelector,
  (dialer) => dialer.displaySettings
);

export const selectDialerIncomingCallsQueue = createSelector(
  dialerSelector,
  (dialer) => dialer.callsQueue
);

export const selectDialerIncomingCall = createSelector(
  dialerSelector,
  (dialer) => ({
    incomingCall: dialer.incomingCall,
    incomingCallDetails: !dialer.incomingCall?.id
      ? undefined
      : dialer.callsQueue.details[dialer.incomingCall.id],
  })
);

export const selectDialerActiveCall = createSelector(
  dialerSelector,
  (dialer) => ({
    activeCall: dialer.activeCall,
    activeCallDetails: !dialer.activeCall?.id
      ? undefined
      : dialer.callsQueue.details[dialer.activeCall.id],
  })
);

export const selectDialerCallsQueue = createSelector(
  dialerSelector,
  (dialer) => dialer.callsQueue
);

export const selectOutgoingCallDetails = createSelector(
  dialerSelector,
  (dialer) => dialer.outgoingCall
);

export const selectIgnoreCallSids = createSelector(
  dialerSelector,
  (dialer) => dialer.ignoreCallSids
);

export const selectVoiceSettings = createSelector(
  dialerSelector,
  (dialer) => dialer.displaySettings.voiceSettings
);

export const selectExecuteCallUpdateSid = createSelector(
  dialerSelector,
  (dialer) =>
    dialer.createCallDetails.callSid &&
    dialer.createCallDetails.isCreateExecuted &&
    dialer.createCallDetails.isShouldExecuteUpdate
      ? dialer.createCallDetails.callSid
      : undefined
);

export default dialerSlice.reducer;
